import { useEffect, useState } from 'react'
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  FolderIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CloudArrowUpIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  BookOpenIcon,
  ScaleIcon,
  DocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

// Map icon names to Heroicons components
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  'book-open': BookOpenIcon,
  'scale': ScaleIcon,
  'document': DocumentIcon,
  'folder': FolderIcon,
}

const getCategoryIcon = (iconName: string | undefined) => {
  if (!iconName) return FolderIcon
  return iconMap[iconName] || FolderIcon
}
import { Card, Badge, Avatar, InlineSpinner, EmptyState, Pagination, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { documentsApi } from '@/api'
import { useAuthStore } from '@/store/authStore'
import type { Document, DocumentCategory, Folder } from '@/api/documents'

// Limiti allineati al backend (Document.title max_length=200) e a nginx (client_max_body_size 520M)
const MAX_DOC_TITLE_CHARS = 200
const MAX_DOC_FILE_MB = 512

/** Estrae un messaggio leggibile da una risposta di errore dell'API. */
function apiErrorMessage(error: any, fallback: string): string {
  const status: number | undefined = error?.response?.status
  const data = error?.response?.data
  if (status === 413) return `Il file supera il limite di ${MAX_DOC_FILE_MB} MB.`
  if (data && typeof data === 'object') {
    if (typeof data.error === 'string') return data.error
    if (typeof data.detail === 'string') return data.detail
    const fieldLabels: Record<string, string> = { title: 'Titolo', file: 'File', description: 'Descrizione', category: 'Categoria' }
    const parts = Object.entries(data)
      .filter(([, v]) => Array.isArray(v) || typeof v === 'string')
      .map(([k, v]) => `${fieldLabels[k] || k}: ${Array.isArray(v) ? v.join(' ') : v}`)
    if (parts.length) return parts.join(' ')
  }
  if (status && status >= 500) return `${fallback} Errore del server (codice ${status}): riprova o segnala il problema.`
  return fallback
}

export default function DocumentsPage() {
  const { user } = useAuthStore()
  const [documents, setDocuments] = useState<Document[]>([])
  const [categories, setCategories] = useState<DocumentCategory[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [downloading, setDownloading] = useState<number | null>(null)

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploadTitle, setUploadTitle] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadCategory, setUploadCategory] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const titleTooLong = uploadTitle.length > MAX_DOC_TITLE_CHARS
  const fileTooBig = !!uploadFile && uploadFile.size > MAX_DOC_FILE_MB * 1024 * 1024

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setUploadError(null)
  }

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchCategories()
    fetchFolders()
  }, [])

  useEffect(() => {
    fetchDocuments()
  }, [currentPage, searchQuery, selectedCategory, selectedFolder])

  const fetchCategories = async () => {
    try {
      const data = await documentsApi.getCategories()
      setCategories(data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const fetchFolders = async () => {
    try {
      const data = await documentsApi.getFolders(selectedFolder || undefined)
      setFolders(data || [])
    } catch (error) {
      console.error('Error fetching folders:', error)
      setFolders([])
    }
  }

  const fetchDocuments = async () => {
    setLoading(true)
    try {
      const data = await documentsApi.getDocuments({
        page: currentPage,
        search: searchQuery || undefined,
        category: selectedCategory || undefined,
        folder: selectedFolder || undefined,
        ordering: '-created_at',
      })
      setDocuments(data.results || [])
      setTotalPages(Math.ceil((data.count || 0) / 20))
      setTotalItems(data.count || 0)
    } catch (error) {
      console.error('Error fetching documents:', error)
      setDocuments([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    fetchDocuments()
  }

  const handleDownload = async (doc: Document) => {
    setDownloading(doc.id)
    try {
      const blob = await documentsApi.downloadDocument(doc.slug)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      // Use file_name which includes extension, fallback to title + extension
      a.download = doc.file_name || `${doc.title}.${doc.file_type}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading document:', error)
    } finally {
      setDownloading(null)
    }
  }

  const handleUpload = async () => {
    if (!uploadFile || !uploadTitle.trim()) return
    if (titleTooLong) {
      setUploadError(`Il titolo è di ${uploadTitle.length} caratteri: il massimo è ${MAX_DOC_TITLE_CHARS}.`)
      return
    }
    if (fileTooBig) {
      setUploadError(`Il file è di ${(uploadFile.size / 1024 / 1024).toFixed(0)} MB e supera il limite di ${MAX_DOC_FILE_MB} MB.`)
      return
    }

    setUploading(true)
    setUploadError(null)
    try {
      const formData = new FormData()
      formData.append('file', uploadFile)
      formData.append('title', uploadTitle)
      if (uploadDescription) formData.append('description', uploadDescription)
      if (uploadCategory) formData.append('category', uploadCategory)

      await documentsApi.uploadDocument(formData)
      closeUploadModal()
      setUploadFile(null)
      setUploadTitle('')
      setUploadDescription('')
      setUploadCategory('')
      fetchDocuments()
    } catch (error) {
      console.error('Error uploading document:', error)
      setUploadError(apiErrorMessage(error, 'Caricamento non riuscito.'))
    } finally {
      setUploading(false)
    }
  }

  const handleDeleteClick = (doc: Document) => {
    setDocumentToDelete(doc)
    setShowDeleteModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return

    setDeleting(true)
    try {
      await documentsApi.deleteDocument(documentToDelete.slug)
      setShowDeleteModal(false)
      setDocumentToDelete(null)
      fetchDocuments()
    } catch (error: any) {
      console.error('Error deleting document:', error)
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Errore durante l\'eliminazione del documento'
      alert(errorMessage)
    } finally {
      setDeleting(false)
      setShowDeleteModal(false)
    }
  }

  const canDeleteDocument = (doc: Document) => {
    if (!user) return false
    const isOwner = doc.uploaded_by.id === user.id
    const isAdmin = user.role === 'admin' || user.role === 'super_admin'
    return isOwner || isAdmin
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileIcon = (fileType: string) => {
    const iconColors: Record<string, string> = {
      pdf: 'text-red-500',
      doc: 'text-blue-500',
      docx: 'text-blue-500',
      xls: 'text-green-500',
      xlsx: 'text-green-500',
      ppt: 'text-orange-500',
      pptx: 'text-orange-500',
      txt: 'text-gray-500',
      csv: 'text-green-500',
    }
    return iconColors[fileType.toLowerCase()] || 'text-gray-500'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  if (loading && currentPage === 1) {
    return <InlineSpinner message="Caricamento documenti..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Repository Documentale</h1>
          <p className="text-gray-600">Consulta e scarica documenti e risorse</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowUploadModal(true)}>
            <CloudArrowUpIcon className="h-5 w-5 mr-2" />
            Carica documento
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Cerca documenti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                showFilters ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <FunnelIcon className="h-5 w-5" />
              Filtri
            </button>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-500'}`}
              >
                <Squares2X2Icon className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${viewMode === 'list' ? 'bg-primary-100 text-primary-700' : 'bg-white text-gray-500'}`}
              >
                <ListBulletIcon className="h-5 w-5" />
              </button>
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Cerca
            </button>
          </div>

          {showFilters && (
            <div className="pt-4 border-t border-gray-200">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoria
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value)
                      setCurrentPage(1)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Tutte le categorie</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.slug}>
                        {cat.name} ({cat.documents_count})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </form>
      </Card>

      {/* Categories Quick Filter */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => {
              setSelectedCategory('')
              setCurrentPage(1)
            }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              !selectedCategory
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Tutti
          </button>
          {categories.map((cat) => {
            const IconComponent = getCategoryIcon(cat.icon)
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setSelectedCategory(cat.slug)
                  setCurrentPage(1)
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-1 ${
                  selectedCategory === cat.slug
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                {cat.name}
              </button>
            )
          })}
        </div>
      )}

      {/* Folders */}
      {folders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {folders.map((folder) => (
            <button
              key={folder.id}
              onClick={() => {
                setSelectedFolder(folder.id)
                setCurrentPage(1)
                fetchFolders()
              }}
              className="flex flex-col items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all"
            >
              <FolderIcon className="h-12 w-12 text-yellow-500 mb-2" />
              <span className="text-sm font-medium text-gray-900 text-center truncate w-full">
                {folder.name}
              </span>
              <span className="text-xs text-gray-500">
                {folder.documents_count} documenti
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 ? (
        <>
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {documents.map((doc) => (
                <Card key={doc.id} className="hover:shadow-lg transition-shadow">
                  {/* File Preview */}
                  <div className="relative h-32 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-lg bg-gray-100 flex items-center justify-center">
                    {doc.thumbnail_url ? (
                      <img
                        src={doc.thumbnail_url}
                        alt={doc.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <DocumentTextIcon className={`h-16 w-16 ${getFileIcon(doc.file_type)}`} />
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge size="sm" className="uppercase">{doc.file_type}</Badge>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{doc.title}</h3>
                    {doc.description && (
                      <p className="text-sm text-gray-500 line-clamp-2">{doc.description}</p>
                    )}

                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <span>{formatDate(doc.created_at)}</span>
                    </div>

                    {doc.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {doc.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} size="sm" variant="default">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        {doc.downloads_count}
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <EyeIcon className="h-5 w-5 text-gray-500" />
                        </a>
                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={downloading === doc.id}
                          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5 text-primary-600" />
                        </button>
                        {canDeleteDocument(doc) && (
                          <button
                            onClick={() => handleDeleteClick(doc)}
                            className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                            title="Elimina documento"
                          >
                            <TrashIcon className="h-5 w-5 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <div className="divide-y divide-gray-100">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 py-4 px-2 -mx-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className={`flex-shrink-0 w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center`}>
                      <DocumentTextIcon className={`h-8 w-8 ${getFileIcon(doc.file_type)}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{doc.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <Badge size="sm" className="uppercase">{doc.file_type}</Badge>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>{formatDate(doc.created_at)}</span>
                        {doc.category && <span>{doc.category.name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Avatar
                        size="sm"
                        name={doc.uploaded_by.full_name}
                        src={doc.uploaded_by.avatar || undefined}
                      />
                      <span className="text-sm text-gray-500">{doc.uploaded_by.full_name}</span>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={doc.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        <EyeIcon className="h-5 w-5 text-gray-500" />
                      </a>
                      <button
                        onClick={() => handleDownload(doc)}
                        disabled={downloading === doc.id}
                        className="p-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5 text-primary-600" />
                      </button>
                      {canDeleteDocument(doc) && (
                        <button
                          onClick={() => handleDeleteClick(doc)}
                          className="p-2 rounded-lg hover:bg-red-100 transition-colors"
                          title="Elimina documento"
                        >
                          <TrashIcon className="h-5 w-5 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
          />
        </>
      ) : (
        <Card>
          <EmptyState
            icon={<DocumentTextIcon className="h-12 w-12" />}
            title={searchQuery || selectedCategory ? 'Nessun documento trovato' : 'Nessun documento'}
            description={
              searchQuery || selectedCategory
                ? 'Prova a modificare i filtri di ricerca'
                : 'I documenti saranno disponibili a breve'
            }
          />
        </Card>
      )}

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={closeUploadModal}
        title="Carica documento"
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                File *
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg">
                <div className="space-y-1 text-center">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500">
                      <span>Seleziona un file</span>
                      <input
                        type="file"
                        className="sr-only"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setUploadFile(file)
                            setUploadError(null)
                            if (!uploadTitle) setUploadTitle(file.name.slice(0, MAX_DOC_TITLE_CHARS))
                          }
                        }}
                      />
                    </label>
                  </div>
                  {uploadFile && (
                    <p className="text-sm text-gray-900">
                      {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(1)} MB)
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Dimensione massima: {MAX_DOC_FILE_MB} MB</p>
                </div>
              </div>
              {fileTooBig && (
                <p className="mt-1 text-sm text-red-600">
                  Il file supera il limite di {MAX_DOC_FILE_MB} MB.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                type="text"
                value={uploadTitle}
                onChange={(e) => {
                  setUploadTitle(e.target.value)
                  setUploadError(null)
                }}
                maxLength={MAX_DOC_TITLE_CHARS}
                aria-invalid={titleTooLong}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                  titleTooLong ? 'border-red-400' : 'border-gray-300'
                }`}
                required
              />
              <p className={`mt-1 text-xs ${titleTooLong ? 'text-red-600' : 'text-gray-500'}`}>
                {uploadTitle.length}/{MAX_DOC_TITLE_CHARS} caratteri
                {titleTooLong ? ' · troppo lungo' : ''}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Nessuna categoria</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {uploadError && (
              <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {uploadError}
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={closeUploadModal}>
            Annulla
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!uploadFile || !uploadTitle.trim() || titleTooLong || fileTooBig || uploading}
          >
            {uploading ? 'Caricamento...' : 'Carica'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Elimina documento"
      >
        <ModalBody>
          <p className="text-gray-600">
            Sei sicuro di voler eliminare il documento <strong>{documentToDelete?.title}</strong>?
          </p>
          <p className="text-sm text-red-600 mt-2">
            Questa azione non può essere annullata.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
